import React from "react";
import { Dropdown, type DropdownProps } from "antd";
import { createGlobalStyle } from "styled-components";
import { POPUP_KEEP_IN_VIEWPORT } from "@/shared/utils/popupOverflow";

const ROOT_CLASS = "app-dropdown";

/*
 * The branded action menu: white 16px-radius panel with the brand-blue shadow and roomy icon rows.
 * Keyed on the root class so plain antd dropdowns elsewhere are untouched.
 */
const AppDropdownStyles = createGlobalStyle`
	.${ROOT_CLASS} .ant-dropdown-menu {
		min-width: 170px;
		max-width: calc(100vw - 32px);
		padding: 0;
		overflow: hidden;
		border-radius: 16px;
		background: #ffffff;
		box-shadow: 0px 4px 12px 0px rgba(21, 89, 165, 0.23);
	}

	.${ROOT_CLASS} .ant-dropdown-menu .ant-dropdown-menu-item {
		gap: 12px;
		margin: 0;
		padding: 14px 18px;
		border-radius: 0;
		color: #35373d;
		font-size: 14px;
		line-height: 20px;
		-webkit-tap-highlight-color: transparent;
	}

	.${ROOT_CLASS} .ant-dropdown-menu .ant-dropdown-menu-item:not(:last-child) {
		margin-bottom: 1px;
	}

	.${ROOT_CLASS} .ant-dropdown-menu .ant-dropdown-menu-item-icon {
		margin-inline-end: 0;
		font-size: 18px;
	}

	.${ROOT_CLASS} .ant-dropdown-menu .ant-dropdown-menu-item:hover,
	.${ROOT_CLASS} .ant-dropdown-menu .ant-dropdown-menu-item-active {
		background: #f7f8fc;
	}

	/* Pressed: the shared row tint (pressedTint), colour only. */
	.${ROOT_CLASS} .ant-dropdown-menu .ant-dropdown-menu-item:active:not(.ant-dropdown-menu-item-disabled) {
		background-image: linear-gradient(rgba(58, 12, 163, 0.1), rgba(58, 12, 163, 0.1));
	}

	/* antd fills danger rows red on hover; keep the neutral hover with red text and icon. */
	.${ROOT_CLASS} .ant-dropdown-menu .ant-dropdown-menu-item-danger,
	.${ROOT_CLASS} .ant-dropdown-menu .ant-dropdown-menu-item-danger:hover,
	.${ROOT_CLASS} .ant-dropdown-menu .ant-dropdown-menu-item-danger.ant-dropdown-menu-item-active {
		color: #d30024;
		background-color: #f7f8fc;
	}

	.${ROOT_CLASS} .ant-dropdown-menu .ant-dropdown-menu-item-danger:not(:hover):not(.ant-dropdown-menu-item-active) {
		background-color: transparent;
	}
`;

export type AppDropdownProps = DropdownProps;

/**
 * antd `Dropdown` with the app's branded menu and popups kept on screen by default
 * (`POPUP_KEEP_IN_VIEWPORT`). Use with `menu` items (`icon`, `danger`, `disabled`) so keyboard
 * navigation, roles and close-on-select come from antd.
 */
export const AppDropdown: React.FC<AppDropdownProps> = ({ rootClassName, autoAdjustOverflow, ...props }) => (
	<>
		<AppDropdownStyles />
		<Dropdown
			{...props}
			autoAdjustOverflow={autoAdjustOverflow ?? POPUP_KEEP_IN_VIEWPORT}
			rootClassName={rootClassName ? `${ROOT_CLASS} ${rootClassName}` : ROOT_CLASS}
		/>
	</>
);
